import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import compose from 'recompose/compose';
import clsx from 'clsx';
import {
  withStyles,
  Link,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener,
  Collapse,
  Button,
} from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { number, bool, func, shape, arrayOf } from 'prop-types';

import {
  fetchAuthSettingsList,
  resetApp,
  updateDefaultAuthSettings,
} from '../../../actions/authenticationSettings';
import { getUserDetails, getFeatureFlagOktaIdp } from '../../../reducers/portalConfig';
import {
  getAuthSettingsListResults,
  getIsError,
  getIsUpdateDefaultSuccess,
  getErrors,
  getIsAuthSaveSuccess,
  getIsAuthUpdateSuccess,
  getIsDeleteSuccess,
} from '../../../reducers/authenticationSettings';
import {
  AUTH_SETTINGS_METHOD,
  ALERT_SUCCESS,
  ERROR_TITLE_TEXT,
  ERROR_TEXT,
} from '../../../constants';
import styles from './styles';
import ListContainer from '../../list';

import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import { get } from 'lodash';

const getLinkContent = (item, classes) => (
  <div>
    <Link underline="none" href={`/publish/settings/authentication/edit/${item.uuid}?authType=${AUTH_SETTINGS_METHOD[item.authKey]}`}>
      {item.name}
    </Link>
    {item.defaultConfig &&
      <span className={classes.tooltipContainer}>
        {getI18nFormattedMessage('label.default')}
      </span>
    }
  </div>
);

const getListColumns = ({ classes, onUpdateDefault, isOktaIdpFlag }) => [{
  id: 'name',
  label: getI18nFormattedMessage('label.scheme.name'),
  minWidth: 400,
  value: (item) => {
    if(item.authKey !== 'label.auth.type.casso')
      return getLinkContent(item, classes);
    else
      return <div>{item.name}</div>
  },
}, {
  id: 'acsUrl',
  label: getI18nFormattedMessage('label.authorize.assertion.acs.url'),
  minWidth: 100,
  isHidden: !isOktaIdpFlag,
  value: (item) => {
    return get(item, 'ConfigurationParams.acsUrl')
  },
}, {
  id: 'authKey',
  label: getI18nFormattedMessage('label.type'),
  minWidth: 400,
  format: value => getI18nFormattedMessage(value),
}, {
  id: 'action',
  label:getI18nFormattedMessage('label.action'),
  value: (item) =>
    !item.defaultConfig && (item.authKey !== 'label.auth.type.jwtpapi' &&
      item.authKey !== 'label.auth.type.casso') ? (
      <Button
        className={classes.defaultButton}
        data-apim-test="make-scheme-default-action"
        id="make-scheme-default-action"
        data-layer7-test="make-scheme-default-action"
        onClick={() => {
          onUpdateDefault(item.uuid);
        }}
        variant="text"
        color="secondary"
      >
        {getI18nFormattedMessage(
          'label.auth.settings.list.column.action.default',
        )}
      </Button>
    ) : null,
  minWidth: 100,
}];

export const ListRaw = (props) => {
  const {
    isLoading,
    userContext,
    results = [],
    classes,
    isUpdateDefaultSuccess,
    isError,
    updateDefaultErrors,
    isSaveSuccess,
    isUpdateSuccess,
    isOktaIdpFlag,
  } = props;

  const intl = getI18n(useIntl());
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  const emptyFunction = () => {};
  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };
  
  useEffect(() => {
    if (!userContext.portalAdmin) { props.push('/404'); }
  }, [userContext]);

  useEffect(() => {
    props.fetchAuthSettingsList();
    const isAuthDelete = localStorage.getItem('isAuthDeleteSuccess');
    if(isAuthDelete === 'true') {
      notifyMessages(
        ALERT_SUCCESS,
        intl.getI18nMessage('label.auth.settings.scheme.delete.success'),
      );
      localStorage.setItem('isAuthDeleteSuccess', false);
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    }
  }, []);

  useEffect(() => {
    if(isUpdateDefaultSuccess) {
      props.fetchAuthSettingsList();
      notifyMessages(
        ALERT_SUCCESS,
        intl.getI18nMessage('label.auth.settings.scheme.update.success'),
      );
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    }
  }, [isUpdateDefaultSuccess]);

  useEffect(() => {
    if(isSaveSuccess || isUpdateSuccess) {
      localStorage.setItem('isUnSavedChanges', false);
      notifyMessages(
        ALERT_SUCCESS,
        isUpdateSuccess ? intl.getI18nMessage('label.auth.settings.edit.scheme.save.success')
        : intl.getI18nMessage('label.auth.settings.create.scheme.save.success'),
      );
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    }
  }, [isSaveSuccess, isUpdateSuccess]);

  useEffect(() => {
    if (isError) {
      notifyMessages(ERROR_TEXT, ERROR_TITLE_TEXT);
      if (updateDefaultErrors.length > 0) {
        setErrors(updateDefaultErrors);
      }
    }
  }, [isError, updateDefaultErrors]);

  const handleClick = () => { setOpen(!open); };
  const handleClickAway = () => { setOpen(false); };

  const onUpdateDefault = (uuid) => {
    props.updateDefaultAuthSettings(uuid);
  }

  const actions = [{
      key: 'jwt',
      value: getI18nFormattedMessage('label.jwt'),
      onClick: () => props.push('/publish/settings/authentication/add?authType=JWT'),
      visible: true,
      disabled: false,
    },{
      key: 'ldap',
      value: getI18nFormattedMessage('label.ldap'),
      onClick: () => props.push('/publish/settings/authentication/add?authType=LDAP'),
      visible: true,
      disabled: false,
    },{
      key: 'samlsso',
      value: getI18nFormattedMessage('label.saml.sso'),
      onClick: () => props.push('/publish/settings/authentication/add?authType=SAML'),
      visible: true,
      disabled: false,
    },
  ];

  const AddActions = () => {
    return(
      <div
        id="auth-settings-actions-container"
        data-apim-test={'auth-settings-actions-container'}
        className={classes.actionsContainer}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <List component="nav">
            <ListItem id="auth-settings-actions-button" data-apim-test="auth-settings-actions-button" classes={{ root: classes.listButton }} button onClick={handleClick}>
              <ListItemText primary={getI18nFormattedMessage('label.auth.settings.add.button')} />
              {open ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse className={classes.collapseContainer} in={open}>
              <List className={classes.list} component="div" disablePadding>
              {actions
                  .filter(({ visible }) => visible)
                  .map(({ key, value, disabled, onClick }) => (
                    <ListItem
                      key={key}
                      id={key}
                      data-apim-test={key}
                      button
                      className={clsx('action', classes.listItem)}
                      disabled={disabled}
                      onClick={onClick}
                    >
                      <ListItemText primary={value} />
                    </ListItem>
                ))}
              </List>
            </Collapse>
          </List>
        </ClickAwayListener>
      </div>
    );
  }

  return (
    <div>
      <ListContainer
        listPageId="auth-settings-list"
        isLoading={isLoading}
        notificationId="auth-settings-notifications"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationStatus}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        pageHeaderTitle={intl.getI18nMessage('label.auth.settings.list.page.title')}
        pageHeaderTooltipTitle={''}
        addButtonLabel={intl.getI18nMessage('label.auth.settings.add.button')}
        ActionsMenu={AddActions}
        showListHeader={true}
        hidePagination={true}
        columns={getListColumns({ classes, onUpdateDefault, isOktaIdpFlag })}
        rows={results}
        noResultsMessage={intl.getI18nMessage('label.no.results')}
        notifyMessages={emptyFunction}
        errors={errors}
      />
    </div>
  );
};

ListRaw.propTypes = {
  classes: shape({}),
  userContext: shape({}),
  isLoading: bool,
  totalPages: number,
  totalElements: number,
  results: arrayOf(shape({})),
  fetchAuthSettingsList: func,
  resetApp: func,
  notifyMessages: func,
  push: func,
  isUpdateDefaultSuccess: bool,
  isError: bool,
  updateDefaultErrors: arrayOf(shape({})),
  updateDefaultAuthSettings: func,
  isSaveSuccess: bool,
  isUpdateSuccess: bool,
  isOktaIdpFlag: bool,
  isDeleteSuccess: bool,
};

const mapStateToProps = (state) => ({
  isLoading: false,
  results: getAuthSettingsListResults(state),
  userContext: getUserDetails(state),
  isUpdateDefaultSuccess: getIsUpdateDefaultSuccess(state),
  isError: getIsError(state),
  updateDefaultErrors: getErrors(state),
  isSaveSuccess: getIsAuthSaveSuccess(state),
  isUpdateSuccess: getIsAuthUpdateSuccess(state),
  isOktaIdpFlag: getFeatureFlagOktaIdp(state),
  isDeleteSuccess: getIsDeleteSuccess(state),
});

const mapDispatchToProps = {
  fetchAuthSettingsList,
  updateDefaultAuthSettings,
  resetApp,
  push,
};
ListRaw.displayName = 'AutheticationsSettingsList';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ListRaw);
